import 'dayjs/locale/es';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { DEFAULT_LOCALE } from '#constants/locales';

import {
  GET_DAY,
  GET_MONTH,
  MAX_MINUTES,
  ROUNDED_MINUTES,
  LOCALES,
  TIME_ZONES,
  WEEKEND_DAYS
} from './constants';

dayjs.extend(utc);

export const currentDate = () => new Date();

export const isSameDate = (date, compareDate) =>
  date.getDate() === compareDate.getDate() &&
  date.getMonth() === compareDate.getMonth() &&
  date.getFullYear() === compareDate.getFullYear();

export const formatTime = time => `0${time}`.slice(-2);

export const dateToTime = date => `${formatTime(date.getHours())}:${formatTime(date.getMinutes())}`;

export const isDate = date => date instanceof Date;

export const todayStringES = GET_DAY.ES[new Date().getDay()];
export const currentYear = currentDate().getFullYear();

export const getUniqueDates = dates =>
  dates.filter((date, index, self) => self.findIndex(compareDate => isSameDate(date, compareDate)) === index);

// Formatting

// DD/MM/YYYY - HH:MM
export const buildDateAndHour = (date, locale = DEFAULT_LOCALE) =>
  `${date.toLocaleDateString(locale)} - ${dateToTime(date)}hs`;

// DD/MM/YYYY
export const buildDateDDMMYYY = date =>
  `${formatTime(date?.getDate?.())}/${formatTime(date?.getMonth?.() + 1)}/${date?.getFullYear?.()}`;

// DD MM
export const buildDateDDMM = (date, separator) =>
  `${formatTime(date?.getDate())} ${separator || ''} ${GET_MONTH.ES[date?.getMonth()]}`;

// DD, dd MM
export const buildDateDDddMM = date => `${GET_DAY.ES[date?.getDay()]}, ${buildDateDDMM(date)}`;

// HH:MM - HH:MMhs
export const buildHourRange = (from, to) => `${dateToTime(new Date(from))} - ${dateToTime(new Date(to))}hs`;

// Setters
export const addMonths = (date, months) => {
  const validDate = new Date(date);
  validDate.setMonth(validDate.getMonth() + months);
  return validDate;
};

// Validations

export const isInvalidDate = (date, invalidDays) => invalidDays.includes(new Date(date).getDay());

export const isSameDateAndTime = (date, compareDate) =>
  date.getTime() === compareDate.getTime() && isSameDate(date, compareDate);

// Ranges

export const isFromToday = date => date === new Date().getDate().toString();

export const addMinutes = (date, minutes) => {
  const validDate = date && new Date(date);
  validDate.setMinutes(validDate.getMinutes() + minutes);
  return validDate;
};

export const roundHour = date => {
  const minutes = date.getMinutes();
  const minutesToAdd = minutes % ROUNDED_MINUTES === 0 ? 0 : ROUNDED_MINUTES - (minutes % ROUNDED_MINUTES);
  return addMinutes(date, minutesToAdd);
};

export const minutesToTime = minutes =>
  minutes < MAX_MINUTES
    ? `${minutes} min`
    : `${Math.floor(minutes / MAX_MINUTES)}:${formatTime(minutes % MAX_MINUTES)} hs`;

export const modifyTime = (date, { hour, minute }) => {
  const dateToModify = new Date(date);
  dateToModify.setHours(hour);
  dateToModify.setMinutes(minute);
  return dateToModify;
};

export const mergeDates = (date, time) =>
  modifyTime(date, { hour: time.getHours(), minute: time.getMinutes() });

export const mergeObjectDates = ({ date, time }) => mergeDates(date, time);

export const convertToDate = string => new Date(string);

// Convert string DD/MM/YYYY - HH:MMhs to Date
export const convertStringToDate = string => {
  const [date, time] = string.split(' - ');
  const formattedTime = time.replace('hs', '');
  const [day, month, year] = date.split('/');
  const [hour, minute] = formattedTime.split(':');
  return new Date(year, month - 1, day, hour, minute);
};

// dayjs extensions

export const getMonthName = (date, locale = DEFAULT_LOCALE) => date.locale(locale).format('MMM');

export const getFullMonthName = (date, locale = DEFAULT_LOCALE) =>
  GET_MONTH[locale.toUpperCase()][date?.month?.() || 0];

export const getMonths = (year = dayjs().year()) => {
  const startOfYear = dayjs().startOf('year').year(year);
  const months = Array.from({ length: 12 }, (_, index) => {
    const month = startOfYear.add(index, 'month');
    return { label: getMonthName(month), value: month, disabled: month.isBefore(dayjs(), 'month') };
  });
  return months;
};

export const getCurrentYear = () => currentDate().getFullYear();

export const getYear = date => date?.year?.() || getCurrentYear();

export const startOfDay = date => dayjs(date).startOf('day').toDate();

export const startOfDayUTC = date => dayjs(date).utcOffset(0).startOf('day').toDate();

export const endOfDay = date => dayjs(date).endOf('day').toDate();

export const endOfDayUTC = date => dayjs(date).utcOffset(0).endOf('day').toDate();

export const startOfMonth = date => dayjs(date).startOf('month').toDate();

export const endOfMonth = date => dayjs(date).endOf('month').toDate();

export const formatDateESDayJs = date => dayjs(date).locale('es').format('DD [de] MMMM [de] YYYY');

export const formatDateESDayJsWithoutYear = date => dayjs(date).locale('es').format('DD [de] MMMM');

// DD/MM/YYYY HH:MM
export const buildDateAndTime = (date, locale = DEFAULT_LOCALE) =>
  `${date.toLocaleDateString(locale)} ${dateToTime(date)}hs`;

export const parseDate = date => ({ hour: date?.getHours(), minute: date?.getMinutes() });

export const mapDate = date => (date?.includes('Z') ? buildDateDDMMYYY(new Date(date)) : date || '');

export const isStartDateGreater = (startDate, endDate) =>
  startDate.date > endDate.date || (startDate.date >= endDate.date && startDate.time > endDate.time);

export const buildDateES = date =>
  `${date.getDate()} de ${GET_MONTH.ES[date.getMonth()]} de ${date.getFullYear()}`;

export const buildFullDateES = date => `${GET_DAY.ES[date.getDay()]} ${buildDateES(date)}`;

export const dateToUTC = date =>
  new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds()
  );

export const formatDate = (date, locale, timeZone = TIME_ZONES.BUENOS_AIRES) =>
  date ? dateToUTC(date).toLocaleDateString(locale, { timeZone }) : undefined;

export const formatDateES = date => formatDate(date, LOCALES.ES_AR, TIME_ZONES.BUENOS_AIRES);

export const addYears = (date, years) => {
  const validDate = new Date(date);
  validDate.setFullYear(validDate.getFullYear() + years);
  return validDate;
};

export const addDays = (date, days) => {
  const validDate = new Date(date);
  validDate.setDate(validDate.getDate() + days);
  return validDate;
};

export const addHours = (date, hours) => {
  const validDate = date && new Date(date);
  validDate.setHours(validDate.getHours() + hours);
  return validDate;
};

export const mapDatesToYears = dates => dates && dates.map(date => new Date(date).getFullYear());

export const isDateTimeBefore = (startDateTime, endDateTime) =>
  new Date(startDateTime).getTime() < new Date(endDateTime).getTime();

export const isBefore = (date, compareDate) => new Date(date) < new Date(compareDate);

export const isAfter = (date, compareDate) => new Date(date) > new Date(compareDate);

export const isBeforeNow = date => isBefore(date, currentDate());

export const isAfterNow = date => isAfter(date, currentDate());

export const isFromCurrentYear = date => new Date(date).getFullYear() === currentYear;

export const isSameWeekDay = (date, day) => new Date(date).getDay() === day;

export const isAValidDay = (date, validDays) => validDays.includes(new Date(date).getDay());

export const isWeekendDay = date => isAValidDay(date, WEEKEND_DAYS);

export const isWorkingDay = date => !isWeekendDay(date);

export const passedMoreThanNdays = (date, numberOfDays) =>
  isBefore(addDays(date, numberOfDays), currentDate());

export const isOutOfRange = (date, initialDate, endDate) =>
  isBefore(date, initialDate) || isAfter(date, endDate);

export const getDatesRange = (startDate, endDate) => {
  const dateArray = [];
  let date = new Date(startDate);
  while (date <= endDate) {
    dateArray.push(new Date(date));
    date = addDays(date, 1);
  }
  return dateArray;
};

export const getFilteredDatesRange = (startDate, endDate, filter) =>
  getDatesRange(startDate, endDate).filter(day => filter(day));

export const getWorkingDatesRange = (startDate, endDate) =>
  getFilteredDatesRange(startDate, endDate, isWorkingDay);

export const getTimeRange = (startDate, endDate) => {
  const startHour = dateToTime(new Date(startDate));
  const endHour = dateToTime(new Date(endDate));
  return `${startHour} - ${endHour}`;
};

export const getNumberOfDaysBetween = (startDate, endDate) => getDatesRange(startDate, endDate).length;

export const isValidDate = date => !isNaN(date) && date instanceof Date;
